import { FC, ReactElement } from 'react';

import { SparkDisplayStatus } from '@sparkplug/lib';

import Button from '@components/buttons/Button';
import CountChip from '@components/chips/CountChip';
import { Inbox as InboxIcon, Send as SendIcon } from '@components/icons';
import SparkStatusIcon from '@components/sparks/SparkStatusIcon';

import './SparkStatusButtons.scss';

const StatusOptions = [
    {
        label: 'Active',
        value: 'active',
        to: '/sparks',
        startIcon: <SparkStatusIcon status="active" />,
    },
    {
        label: 'Upcoming',
        value: 'upcoming',
        to: '/sparks/upcoming',
        startIcon: <SparkStatusIcon status="upcoming" />,
    },
    {
        label: 'Complete',
        value: 'complete',
        to: '/sparks/complete',
        startIcon: <SparkStatusIcon status="completed" />,
    },
] as const;
type CoreStatusOption = typeof StatusOptions[number]['value'];

const AdditionalOptions = [
    {
        value: 'inbox/sent',
    },
] as const;

type Option = {
    value: string;
    to?: string;
    startIcon?: ReactElement;
    endIcon?: ReactElement;
    label?: string;
};

const getInboxOverrideData = (
    isVendorAccount: boolean,
    inboxCount?: number,
): {
    startIcon: ReactElement;
    endIcon?: ReactElement;
    label: string;
    to: string;
} => {
    const iconClassName = 'status-icon icon-color-blue';

    return {
        startIcon: isVendorAccount ? (
            <SendIcon className={iconClassName} />
        ) : (
            <InboxIcon className={iconClassName} />
        ),
        endIcon: inboxCount ? <CountChip count={inboxCount} /> : undefined,
        label: isVendorAccount ? 'Sent' : 'Inbox',
        to: isVendorAccount ? '/sparks/sent' : '/sparks/inbox',
    };
};

type OverrideOptions = {
    startIcon?: ReactElement;
    endIcon?: ReactElement;
    label?: string;
};

const SparkStatusButton = ({
    option,
    value,
    onSelect,
    optionOverride = {},
}: {
    option: Option;
    value: SparkDisplayStatus;
    onSelect: (v: Option) => void;
    optionOverride?: OverrideOptions;
}) => {
    const optionMerged = {
        ...option,
        ...optionOverride,
    };

    return (
        <Button
            color="neutral"
            startIcon={optionMerged?.startIcon || null}
            endIcon={optionMerged?.endIcon || null}
            variant="smooth"
            className={value === optionMerged.value ? 'active' : ''}
            onClick={() => onSelect && onSelect(optionMerged)}
        >
            {optionMerged.label}
        </Button>
    );
};

interface SparkStatusButtonsProps {
    onSelect: (v: Option) => void;
    value: SparkDisplayStatus;
    isBrand: boolean;
    unclaimedCount?: number;
    inboxCount?: number;
    isAdmin?: boolean;
    connectEnabled?: boolean;
}

export const SparkStatusButtons: FC<SparkStatusButtonsProps> = ({
    onSelect,
    value,
    isBrand: isVendorAccount,
    unclaimedCount = 0,
    inboxCount,
    isAdmin = false,
    connectEnabled = true,
}) => {
    const optionOverrideMap = {} as Record<CoreStatusOption, OverrideOptions>;

    if (unclaimedCount > 0) {
        optionOverrideMap.complete = {
            endIcon: <CountChip count={unclaimedCount} />,
        };
    }

    return (
        <div className="spark-view__status-buttons">
            <div className="status-tab-group tab-group">
                {StatusOptions.map((option) => (
                    <SparkStatusButton
                        key={option.value}
                        onSelect={onSelect}
                        value={value}
                        option={option}
                        optionOverride={optionOverrideMap[option.value]}
                    />
                ))}
            </div>
            {isAdmin && connectEnabled && (
                <div className="tab-group">
                    {AdditionalOptions.map((option) => (
                        <SparkStatusButton
                            key={option.value}
                            onSelect={onSelect}
                            value={value}
                            option={option}
                            optionOverride={getInboxOverrideData(isVendorAccount, inboxCount)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default SparkStatusButtons;
